import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { MeetingMember } from './useMeetingMembers';
import {
    MeetingParticipantAvatarSlot,
    MeetingParticipantMenu,
    MeetingParticipantStatusIcons,
} from './MeetingParticipantRow';

interface Props {
    member: MeetingMember;
    lazyLoad: boolean;
}

/**
 * A guest is still a live meeting participant, not a lesser one — this reuses
 * MeetingParticipantRow's avatar slot (phone-icon fallback for a dial-in
 * caller) and host-controls menu (mute / turn off camera) rather than
 * maintaining a parallel, thinner row. A dial-in participant is always a
 * guest once the meeting has a room (they never get a chatUserKey), so
 * losing these here would mean losing them for exactly the participant a
 * host is most likely to need to mute.
 */
const MeetingGuestParticipantRow = ({ member, lazyLoad }: Props) => {
    const { i18next } = useAppContext();

    const name =
        member.memberIsCaller && !member.displayName
            ? member.callerId
            : member.displayName;

    return (
        <div className="flex items-center gap-x-3 rounded-sm px-6 py-2 text-base hover:bg-gray-700">
            <MeetingParticipantAvatarSlot member={member} lazyLoad={lazyLoad} />
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">
                    {name}
                    {member.memberIsSelf && (
                        <span className="text-gray-400">
                            {` (${i18next.t('chat.you')})`}
                        </span>
                    )}
                </span>
                <span className="text-xs text-gray-400">
                    {i18next.t(
                        member.isInternal
                            ? 'meetings.participants.internal_guest'
                            : 'meetings.participants.external_guest',
                    )}
                </span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
                <MeetingParticipantStatusIcons member={member} />
            </div>
            <MeetingParticipantMenu member={member} />
        </div>
    );
};

export default React.memo(MeetingGuestParticipantRow);
